import { Box, Table, Badge, IconButton, Text, Flex } from '@chakra-ui/react'
import { Pencil, Trash2 } from 'lucide-react'
import { useMeterReadings, useDeleteMeterReading } from '../hooks'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { accountingToaster as toaster } from '../pages/AccountingLayout'

interface MeterReadingsTableProps {
  distributionKeyId?: string
  fiscalYearId?: string
}

export function MeterReadingsTable({ distributionKeyId, fiscalYearId }: MeterReadingsTableProps) {
  const { data: readings, isLoading } = useMeterReadings(distributionKeyId, fiscalYearId)
  const deleteMutation = useDeleteMeterReading()

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce relevé ?')) return

    try {
      await deleteMutation.mutateAsync(id)
      toaster.create({
        title: 'Succès',
        description: 'Relevé supprimé',
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la suppression',
        type: 'error',
      })
    }
  }

  if (isLoading) {
    return <Text fontSize="sm" color="gray.500">Chargement...</Text>
  }

  if (!readings || readings.length === 0) {
    return (
      <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
        <Text fontSize="sm" color="gray.600">
          Aucun relevé saisi pour le moment
        </Text>
      </Box>
    )
  }

  // Calculer le total
  const totalConsumption = readings.reduce((sum, r) => sum + Number(r.consumption), 0)

  return (
    <Box>
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Lot</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Index début</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Index fin</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Consommation</Table.ColumnHeader>
            <Table.ColumnHeader>Date relevé</Table.ColumnHeader>
            <Table.ColumnHeader>Statut</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">Actions</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {readings.map((reading) => (
            <Table.Row key={reading.id}>
              <Table.Cell fontWeight="medium">
                {reading.unit?.unit_number || '-'}
              </Table.Cell>
              <Table.Cell textAlign="right" fontFamily="mono" fontSize="sm">
                {reading.index_start != null ? Number(reading.index_start).toLocaleString('fr-FR') : '-'}
              </Table.Cell>
              <Table.Cell textAlign="right" fontFamily="mono" fontSize="sm">
                {Number(reading.index_end).toLocaleString('fr-FR')}
              </Table.Cell>
              <Table.Cell textAlign="right" fontFamily="mono" fontSize="sm" fontWeight="semibold">
                {Number(reading.consumption).toLocaleString('fr-FR')}
              </Table.Cell>
              <Table.Cell fontSize="sm">
                {format(new Date(reading.reading_date), 'dd/MM/yyyy', { locale: fr })}
              </Table.Cell>
              <Table.Cell>
                {reading.is_estimated ? (
                  <Badge size="sm" colorPalette="orange">
                    Estimé
                  </Badge>
                ) : (
                  <Badge size="sm" colorPalette="green">
                    Réel
                  </Badge>
                )}
              </Table.Cell>
              <Table.Cell textAlign="center">
                <Flex gap={1} justify="center">
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label="Modifier"
                    title="Modifier"
                    disabled
                  >
                    <Pencil size={14} />
                  </IconButton>
                  <IconButton
                    size="xs"
                    variant="ghost"
                    colorPalette="red"
                    aria-label="Supprimer"
                    title="Supprimer"
                    onClick={() => handleDelete(reading.id)}
                  >
                    <Trash2 size={14} />
                  </IconButton>
                </Flex>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row bg="gray.50">
            <Table.Cell fontWeight="bold">Total</Table.Cell>
            <Table.Cell />
            <Table.Cell />
            <Table.Cell textAlign="right" fontFamily="mono" fontWeight="bold">
              {totalConsumption.toLocaleString('fr-FR')}
            </Table.Cell>
            <Table.Cell />
            <Table.Cell />
            <Table.Cell />
          </Table.Row>
        </Table.Footer>
      </Table.Root>
    </Box>
  )
}
